import type { ClassificationResult } from '@classification-testing/domain';
import { Badge, BadgeSize, BadgeVariant } from '@soteria/design-system-ve';

import * as styles from './ClassificationResultDialog.css';

// Helper to get policy counts
function getPolicyCounts(result: ClassificationResult) {
  const triggeredCount = result.policies?.length || 0;
  const nonTriggeredCount = result.nonTriggeredPolicies?.length || 0;
  const totalPolicies = triggeredCount + nonTriggeredCount;
  const isCompliant = triggeredCount === 0;

  return { triggeredCount, nonTriggeredCount, totalPolicies, isCompliant };
}

// Policy Overall Status component
export function PolicyOverallStatus({ result }: { result: ClassificationResult }) {
  const { triggeredCount, nonTriggeredCount, totalPolicies, isCompliant } = getPolicyCounts(result);

  if (totalPolicies === 0) {
    return null;
  }

  const tooltipText = `${nonTriggeredCount} nontriggered, ${triggeredCount} triggered out of ${totalPolicies} policies`;

  return (
    <div className={styles.policyOverallStatus}>
      <Badge size={BadgeSize.Small} text={isCompliant ? 'ALLOWED' : 'REJECTED'} />
      <span className={styles.policyViolationCount} title={tooltipText}>
        ({triggeredCount}/{totalPolicies}) Violations
      </span>
    </div>
  );
}

// Policy subsection component
export function PolicySubsection({
  title,
  policies,
  showTriggeredBadge,
  maxDisplayed = 10,
}: {
  title: string;
  policies: ClassificationResult['policies'];
  showTriggeredBadge: boolean;
  maxDisplayed?: number;
}) {
  if (!policies || policies.length === 0) {
    return null;
  }

  const displayedPolicies = policies.slice(0, maxDisplayed);
  const remainingCount = policies.length - maxDisplayed;

  return (
    <div className={styles.policySubsection}>
      <h4 className={styles.policySubsectionTitle}>
        {title} ({policies.length})
      </h4>
      <div className={styles.policyList}>
        {displayedPolicies.map(policy => (
          <div key={policy.id} className={styles.policyBadgeContainer}>
            <Badge size={BadgeSize.Small} text={policy.name} title={policy.description} />
            {showTriggeredBadge && <Badge size={BadgeSize.Small} text="TRIGGERED" variant={BadgeVariant.Warning} />}
          </div>
        ))}
        {remainingCount > 0 && (
          <div className={styles.morePoliciesText}>
            + {remainingCount} more {remainingCount === 1 ? 'policy' : 'policies'}
          </div>
        )}
      </div>
    </div>
  );
}

// Policies section component - combines triggered and non-triggered policies with overall status
export function PoliciesSection({ result }: { result: ClassificationResult }) {
  const hasTriggeredPolicies = result.policies && result.policies.length > 0;
  const hasNonTriggeredPolicies = result.nonTriggeredPolicies && result.nonTriggeredPolicies.length > 0;

  // Don't show section if there are no policies at all
  if (!hasTriggeredPolicies && !hasNonTriggeredPolicies) {
    return null;
  }

  // Use two-column layout when both triggered and nontriggered policies exist
  const useTwoColumnLayout = hasTriggeredPolicies && hasNonTriggeredPolicies;

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Policies</h3>

      {/* Overall Policy Status */}
      <PolicyOverallStatus result={result} />

      {/* Policy subsections - two columns if both exist, otherwise single column */}
      <div className={useTwoColumnLayout ? styles.policySectionsRow : undefined}>
        {/* Triggered Policies Subsection */}
        {hasTriggeredPolicies && <PolicySubsection showTriggeredBadge policies={result.policies} title="Triggered Policies" />}

        {/* Nontriggered Policies Subsection */}
        {hasNonTriggeredPolicies && <PolicySubsection policies={result.nonTriggeredPolicies} showTriggeredBadge={false} title="Nontriggered Policies" />}
      </div>
    </div>
  );
}
